import React, { useMemo, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useCreateDepartment,
  useDepartments
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus } from "../common/academicUtils";

const DepartmentList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { data: departments = [], isPending, refetch } = useDepartments();
  const createDepartment = useCreateDepartment();
  const [name, setName] = useState("");

  const sorted = useMemo(
    () => [...departments].sort((a, b) => a.name.localeCompare(b.name)),
    [departments]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-departments", "Loading departments...")}
      />
    );
  }

  return (
    <Box data-testid="department-list-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("departments-title", "Departments")}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          label={t("department-name-label", "Department name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <AppButton
          disabled={!name.trim() || createDepartment.isPending}
          onClick={async () => {
            await createDepartment.mutateAsync({ name: name.trim() });
            setName("");
            refetch();
          }}
        >
          {t("add-department", "Add department")}
        </AppButton>
      </Stack>

      {sorted.length === 0 ? (
        <Typography color="text.secondary">
          {t("departments-empty", "No departments yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {sorted.map((department) => (
            <Box
              component="li"
              key={department.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {department.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatEntityStatus(department.status)}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(department.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DepartmentList;
